<template>
  <EditorModal
    :title="t('automatedScenarios.workflowSelector.title')"
    :show-close-button="true"
    @on-close-modal="closeModal"
  >
    <template #content>
      <div class="scenario-selector-layout">
        <!-- Left Sidebar Filters -->
        <div class="filters-sidebar">
          <div class="filters-sidebar__content">
            <!-- Search -->
            <div class="filter-section">
              <div class="search-wrapper">
                <i class="fa fa-search search-icon" />
                <input
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  :placeholder="t('automatedScenarios.workflowSelector.search.placeholder')"
                >
                <div
                  v-if="searchQuery"
                  class="clear-search-btn"
                  @click="searchQuery = ''"
                >
                  <i class="fa fa-times" />
                </div>
              </div>
            </div>

            <!-- Tags Filter — grouped by category -->
            <div
              v-for="group in tagGroups"
              :key="group.key"
              class="filter-section filter-section--tags"
            >
              <label class="filter-section__label filter-section__label--small">
                {{ group.label }}
              </label>
              <div class="filter-tags">
                <div
                  v-for="tag in group.tags"
                  :key="tag.value"
                  class="filter-tag"
                  :class="{
                    'filter-tag--active': selectedTags.includes(tag.value),
                    [`filter-tag--${group.key}`]: true,
                  }"
                  @click="toggleTag(tag.value)"
                >
                  <span class="filter-tag__label">{{ tag.value }}</span>
                  <span class="filter-tag__count">{{ tag.count }}</span>
                </div>
              </div>
            </div>

            <!-- Author Filter — visual card list with separate groups
                 for ShopiMind vs partner integrations. Integrations get
                 their own scrollable section + an inline search input
                 once the list exceeds 5 entries (typical case when many
                 partners are installed). -->
            <div class="filter-section filter-section--authors">
              <label class="filter-section__label">
                <i class="fa-light fa-user" />
                {{ t('automatedScenarios.workflowSelector.filters.author') }}
              </label>

              <!-- Inline search once the list is long. -->
              <div
                v-if="authorFilterOptions.length > 6"
                class="author-list__search"
              >
                <i class="fa-light fa-search" />
                <input
                  v-model="authorSearchQuery"
                  type="text"
                  :placeholder="t('automatedScenarios.workflowSelector.filters.searchAuthor')"
                >
              </div>

              <!-- Unified author list: ShopiMind + integrations, same level. -->
              <div class="author-list">
                <div
                  v-for="option in filteredAuthorOptions"
                  :key="option.value"
                  class="author-list__item"
                  :class="{
                    'author-list__item--integration': option.value !== 'shopimind' && option.value !== 'me',
                    'author-list__item--active': selectedAuthors.includes(option.value),
                  }"
                  @click="toggleAuthor(option.value)"
                >
                  <span class="author-list__avatar">
                    <img
                      v-if="getAuthorIcon(option.value)"
                      :src="getAuthorIcon(option.value)"
                      :alt="option.label"
                    >
                    <i
                      v-else
                      :class="option.value === 'shopimind' ? 'fa-light fa-user' : 'fa-solid fa-plug'"
                    />
                  </span>
                  <span class="author-list__label">{{ option.label }}</span>
                  <span
                    v-if="option.count !== undefined"
                    class="author-list__count"
                  >{{ option.count }}</span>
                  <span
                    v-if="selectedAuthors.includes(option.value)"
                    class="author-list__check"
                  >
                    <i class="fa-solid fa-check" />
                  </span>
                </div>
                <div
                  v-if="filteredAuthorOptions.length === 0"
                  class="author-list__empty"
                >
                  {{ t('automatedScenarios.workflowSelector.filters.noAuthorMatch') }}
                </div>
              </div>
            </div>

            <!-- Results count -->
            <div class="filter-results">
              <span class="filter-results__count">{{ filteredList.length }}</span>
              <span class="filter-results__label">{{ t('automatedScenarios.workflowSelector.filters.results') }}</span>
            </div>
          </div>
        </div>

        <!-- Scenarios Grid -->
        <main class="scenarios-content">
          <!-- Drill-in banner — shown when filtered to a single integration (G10). -->
          <DrilldownBanner
            v-if="drilledInIntegration"
            :name="drilledInIntegration.name"
            :icon="drilledInIntegration.icon"
            @back="clearAuthorFilter"
          />

          <Loader v-if="isListLoading" />

          <div
            v-else
            class="scenarios-grid"
          >
            <!-- Start from scratch card — hidden when drilled into an
                 integration (it's irrelevant in a single-partner view). -->
            <div
              v-if="!drilledInIntegration"
              class="scenario-card scenario-card--scratch"
            >
              <div class="scenario-card__content">
                <div class="scenario-card__image scenario-card__image--scratch">
                  <div class="scratch-icon-wrapper">
                    <i class="fa-light fa-plus" />
                  </div>
                </div>
                <div class="scenario-card__body">
                  <h3 class="scenario-card__title">
                    {{ t('automatedScenarios.workflowSelector.startFromScratch.title') }}
                  </h3>
                </div>

                <!-- Hover overlay -->
                <div class="scenario-card__hover-overlay">
                  <p class="scenario-card__hover-legend">
                    {{ t('automatedScenarios.workflowSelector.startFromScratch.description') }}
                  </p>
                  <div class="scenario-card__hover-actions">
                    <Button
                      :label="t('automatedScenarios.workflowSelector.actions.choose')"
                      class="p-button-sm p-button-success choose-btn"
                      @click="handleStartFromScratch"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Scenario cards from API (+ integration collection cards) -->
            <template
              v-for="record of displayItems"
              :key="record.__collection ? ('coll-' + record.slug) : record.id"
            >
              <IntegrationCollectionCard
                v-if="record.__collection"
                :name="record.name"
                :icon="record.icon"
                :count="record.count"
                :status="record.status"
                :previews="record.previews"
                :kicker="t('automatedScenarios.workflowSelector.byPartner')"
                :count-label="t('integrations.collection.count', { count: record.count })"
                :open-label="t('integrations.collection.open', { count: record.count })"
                :install-label="t('integrations.collection.install')"
                @open="drillIntoIntegration(record.slug)"
              />
              <div
                v-else
                class="scenario-card"
                :class="{ 'scenario-card--integration': record.author === 'integration' }"
              >
                <div class="scenario-card__content">
                  <!-- Partner ribbon — only on integration items. Sits over
                     the preview image, signals partner provenance loud and
                     early before the user even reads the title. -->
                  <div
                    v-if="record.author === 'integration' && record.integration_name && !drilledInIntegration"
                    class="scenario-card__partner-ribbon"
                  >
                    <div class="scenario-card__partner-logo">
                      <img
                        v-if="record.integration_icon_url"
                        :src="record.integration_icon_url"
                        :alt="record.integration_name"
                      >
                      <i
                        v-else
                        class="fa-solid fa-plug"
                      />
                    </div>
                    <div class="scenario-card__partner-text">
                      <span class="scenario-card__partner-kicker">
                        {{ t('automatedScenarios.workflowSelector.byPartner') }}
                      </span>
                      <span class="scenario-card__partner-name">{{ record.integration_name }}</span>
                    </div>
                  </div>

                  <!-- Image -->
                  <div class="scenario-card__image">
                    <div
                      v-if="record.images_templates && record.images_templates.length > 0"
                      class="scenario-card__preview"
                      :style="`background-image: url(${record.images_templates[0]})`"
                    />
                    <div
                      v-else
                      class="scenario-card__preview scenario-card__preview--placeholder"
                    >
                      <i class="fa fa-image" />
                    </div>
                  </div>

                  <!-- Default: title + author -->
                  <div class="scenario-card__body">
                    <h3 class="scenario-card__title">
                      {{ getLocalizedText(record.name, 'Sans titre') }}
                    </h3>
                    <div
                      v-if="record.author !== 'integration'"
                      class="scenario-card__author"
                    >
                      <i class="fa-light fa-user" />
                      <span>ShopiMind</span>
                    </div>
                  </div>

                  <!-- Hover overlay: description + tags + actions -->
                  <div class="scenario-card__hover-overlay">
                    <p
                      v-if="getLocalizedText(record.legend)"
                      class="scenario-card__hover-legend"
                    >
                      {{ getLocalizedText(record.legend) }}
                    </p>
                    <div
                      v-if="record.tags && record.tags.length"
                      class="scenario-card__hover-tags"
                    >
                      <span
                        v-for="tag in record.tags"
                        :key="tag"
                        class="tag-badge"
                        :class="`tag-badge--${getTagCategory(tag)}`"
                      >
                        {{ tag }}
                      </span>
                    </div>
                    <div class="scenario-card__hover-actions">
                      <Button
                        class="p-button-sm p-button-outlined preview-btn"
                        @click.stop="openGalleryHtmlPreview(record)"
                      >
                        <i class="fa-light fa-eye" />
                      </Button>
                      <Button
                        :label="t('automatedScenarios.workflowSelector.actions.choose')"
                        class="p-button-sm p-button-success choose-btn"
                        @click="handleChoose(record)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </main>
      </div>
    </template>
  </EditorModal>

  <TemplateHtmlPreviewSlider
    v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
    :previews="htmlPreviewData.previews"
    @close="closeHtmlPreview"
  />

  <RequireIntegrationDialog
    v-model:visible="requireIntegrationVisible"
    :integration="requireIntegrationData"
    :status-label="requireIntegrationStatus"
  />
</template>

<script lang="ts">
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import {
  defineComponent, onMounted, Ref, ref, SetupContext, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
// CampaignsCategoryEnum no longer needed — replaced by tags system
import EditorModal from '@/components/modals/EditorModal.vue';
import Loader from '@/components/layout/Loader.vue';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getNowFormattedByTimezone } from '@/helpers/Date';
import { openAutomationCampaignEditor } from '@/views/workflows/AutomationCampaignsActions/useAutomationCampaignsActions';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { store } from '@/store';
import { getCampaignsAutomationBaseList } from '@/composables/workflows/Workflow';
import { UserState } from '@/composables/User';
import { nestCall, NestApiType } from '@/composables/nestApi';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import type { IntegrationOwnedItem } from '@/types/integrations';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface CampaignsAutomationBase extends IntegrationOwnedItem {
  id: number;
  name: Record<string, string>;
  legend: Record<string, string>;
  tags: string[];
  categories?: string; // legacy compat
  path: string;
  images_templates: string[] | null;
}

type TagCategory = 'channel' | 'feature' | 'type' | 'objective';

const TAG_CATEGORY_MAP: Record<string, TagCategory> = {
  // Channels
  Email: 'channel',
  SMS: 'channel',
  Push: 'channel',
  WhatsApp: 'channel',
  Popup: 'channel',
  // Features
  Gamification: 'feature',
  'Bon de reduction': 'feature',
  'A/B Test': 'feature',
  Segmente: 'feature',
  'Cross-canal': 'feature',
  // Scenario types
  'Panier abandonne': 'type',
  'Produit consulte': 'type',
  Anniversaire: 'type',
  Bienvenue: 'type',
  Reactivation: 'type',
  Fidelite: 'type',
  'Incitation newsletter': 'type',
  // Objectives
  Conversion: 'objective',
  Fidelisation: 'objective',
  Retention: 'objective',
  Acquisition: 'objective',
};

export default defineComponent({
  name: 'CampaignAutomationSelectorV2',

  components: {
    TemplateHtmlPreviewSlider,
    Loader,
    EditorModal,
    Button,
    MultiSelect,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  emits: {
    'on-close-dialog': null,
  },

  setup(props: { }, context: SetupContext) {
    const { t, locale } = useI18n();

    const currentLocale = computed(() => locale.value || 'fr');

    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    const isListLoading = ref(false);
    const initialList: Ref<CampaignsAutomationBase[]> = ref([]);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);
    const searchQuery = ref('');

    // Integration gating (modal state, pre-flight check, backend safety
    // net, dynamic author options) — shared with the bulk and template
    // pickers via `useIntegrationGating`.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    const authorFilterOptions = authorFilterOptionsFor(() => initialList.value);

    // ----- Split + search for the visual author filter -----

    /** Live-search over the unified author list (ShopiMind + integrations). */
    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    /**
     * Toggle behaviour for the visual author cards (replaces the multi-
     * select). Clicking a card adds/removes its slug from `selectedAuthors`.
     */
    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    /**
     * Resolve the icon URL for an author option. For ShopiMind we use a
     * font-awesome icon (rendered in the template). For integrations we
     * dig back into `initialList.value` and grab the first item whose
     * `integration_slug` matches — that's where the icon lives.
     */
    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind') return undefined;
      const match = initialList.value.find(
        (item) => item.integration_slug === slug,
      );
      return match?.integration_icon_url ?? undefined;
    };

    // Tags filter
    const selectedTags = ref<string[]>([]);

    const tagGroups = computed(() => {
      const selected = selectedTags.value;

      // Count tags only on items that already match all selected tags
      const tagCounts = new Map<string, number>();
      for (const item of initialList.value) {
        const itemTags = item.tags || [];
        // Item must have all currently selected tags
        if (!selected.every((t) => itemTags.includes(t))) continue;
        for (const tag of itemTags) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      }

      const groups: { key: TagCategory; label: string; tags: { value: string; count: number }[] }[] = [
        { key: 'type', label: t('automatedScenarios.workflowSelector.filters.tagGroups.type'), tags: [] },
        { key: 'channel', label: t('automatedScenarios.workflowSelector.filters.tagGroups.channel'), tags: [] },
        { key: 'feature', label: t('automatedScenarios.workflowSelector.filters.tagGroups.feature'), tags: [] },
        { key: 'objective', label: t('automatedScenarios.workflowSelector.filters.tagGroups.objective'), tags: [] },
      ];

      for (const [value, count] of tagCounts) {
        const cat = TAG_CATEGORY_MAP[value] || 'feature';
        const group = groups.find((g) => g.key === cat);
        if (group) group.tags.push({ value, count });
      }

      for (const group of groups) {
        group.tags.sort((a, b) => a.value.localeCompare(b.value));
      }

      return groups.filter((g) => g.tags.length > 0);
    });

    const toggleTag = (tag: string) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx >= 0) {
        selectedTags.value.splice(idx, 1);
      } else {
        selectedTags.value.push(tag);
      }
    };

    const getTagCategory = (tag: string): string => TAG_CATEGORY_MAP[tag] || 'feature';

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const handleStartFromScratch = async () => {
      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        // Generate scenario name with current date
        const name = `${t('automationPrefix')} - ${getNowFormattedByTimezone()}`;

        // Create empty automation campaign via API
        const result = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/campaigns/automations',
          {},
          '',
          { name },
        );

        if (result && result.data && result.data.id) {
          // Use shared function to open the editor with all checks
          await openAutomationCampaignEditor(
            result.data.id,
            result.data.name,
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Campaign creation error:', error);
        showToastError(t('automatedScenarios.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    // Computed filtered list
    const filteredList = computed(() => {
      if (!initialList.value.length) return [];

      return initialList.value.filter((baseWorkflow) => {
        const tags = baseWorkflow.tags || [];

        // Filter by selected tags (AND: all selected tags must be present)
        const matchedTags = selectedTags.value.length === 0
          || selectedTags.value.every((tag) => tags.includes(tag));

        // Filter by search
        const matchedSearch = searchQuery.value.trim() === ''
          || getLocalizedText(baseWorkflow.name).toLowerCase().includes(searchQuery.value.toLowerCase())
          || getLocalizedText(baseWorkflow.legend).toLowerCase().includes(searchQuery.value.toLowerCase());

        // Filter by author — "shopimind" matches native items (no
        // integration_slug); an integration slug matches its items.
        const authorKey = baseWorkflow.author === 'integration'
          ? baseWorkflow.integration_slug ?? ''
          : 'shopimind';
        const matchedAuthor = selectedAuthors.value.length === 0
          || selectedAuthors.value.includes(authorKey);

        return matchedTags && matchedSearch && matchedAuthor;
      });
    });

    // Collapse an integration's many predefined items into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    const handleChoose = async (record: CampaignsAutomationBase) => {
      // Pre-flight: bail out if the integration isn't active on this shop.
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          {
            idShop,
            type: 'campaign_automation',
            baseId: record.path,
          },
        );

        // Backend safety net (shop may have deactivated the integration
        // between page load and click).
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('automatedScenarios.create_action.success'));
          setRefreshScenariosList(true);
          await openAutomationCampaignEditor(
            asInt(result.id),
            result.name || '',
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Workflow creation error:', error);
        showToastError(t('automatedScenarios.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const openGalleryHtmlPreview = async (record: CampaignsAutomationBase) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(t('automatedScenarios.workflowSelector.preview.error'));
        }
      } catch {
        showToastError(t('automatedScenarios.workflowSelector.preview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    onMounted(async () => {
      isListLoading.value = true;

      try {
        const idShop = UserState.activeShop?.id ?? undefined;
        const result: CampaignsAutomationBase[] = await getCampaignsAutomationBaseList(idShop);

        if (result) {
          initialList.value = result;
        }
      } catch (error) {
        showToastError(t('automatedScenarios.list.error'));
      } finally {
        isListLoading.value = false;
      }
    });

    return {
      t,
      filteredList,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      htmlPreviewData,
      isListLoading,
      searchQuery,
      closeModal,
      handleChoose,
      openGalleryHtmlPreview,
      closeHtmlPreview,
      handleStartFromScratch,
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      selectedTags,
      tagGroups,
      toggleTag,
      getTagCategory,
      getLocalizedText,
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
    };
  },
});
</script>

<style scoped lang="scss">
// ==========================================
// Scenario selector layout (sidebar left)
// ==========================================
.scenario-selector-layout {
  display: block;
  background: #f5f7fa;
  min-height: 100%;
  padding: 1.5rem;
  padding-left: calc(280px + 3rem);
  position: relative;
}

// Left sidebar - Filters (floating style with position fixed)
.filters-sidebar {
  width: 280px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: fit-content;
  position: fixed;
  top: calc(60px + 1.5rem);
  left: 1.5rem;
  z-index: 100;
  max-height: calc(100vh - 60px - 3rem);
  overflow-y: auto;

  &__content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &--tags {
    gap: 0.5rem;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    i {
      font-size: 1rem;
      color: #94c748;
    }

    &--small {
      font-size: 0.6875rem;
      color: #94a3b8;
      text-transform: none;
      letter-spacing: 0;
    }
  }
}

// Search wrapper
.search-wrapper {
  position: relative;
  width: 100%;
}

// Search input
.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94c748;
  font-size: 1rem;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.75rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:hover {
    border-color: #cbd5e1;
    background: #ffffff;
  }

  &:focus {
    border-color: #94c748;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(148, 199, 72, 0.12);
  }
}

.clear-search-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0;
  border: none;
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  color: #64748b;

  &:hover {
    background: #94c748;
    color: #ffffff;
  }

  i {
    font-size: 0.5625rem;
  }
}

// Tags filter (sidebar)
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.625rem;
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &__label {
    line-height: 1.2;
  }

  &__count {
    font-size: 0.625rem;
    opacity: 0.7;
    font-weight: 600;
  }

  // Category colors (default state)
  &--channel {
    background: rgba(59, 130, 246, 0.1);
    color: #2563eb;
    &.filter-tag--active { background: #3b82f6; color: #fff; }
  }
  &--feature {
    background: rgba(139, 92, 246, 0.1);
    color: #7c3aed;
    &.filter-tag--active { background: #8b5cf6; color: #fff; }
  }
  &--type {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
    &.filter-tag--active { background: #f59e0b; color: #fff; }
  }
  &--objective {
    background: rgba(34, 197, 94, 0.1);
    color: #16a34a;
    &.filter-tag--active { background: #22c55e; color: #fff; }
  }

  &:hover {
    transform: scale(1.05);
  }

  &--active {
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);

    .filter-tag__count {
      opacity: 0.9;
    }
  }
}

// ============ Author filter — visual card list ============
// Replaces the previous MultiSelect. Each option is a clickable card
// with the author's avatar (logo for integrations, generic icon for
// ShopiMind) so partners are recognisable at a glance instead of
// hiding behind a generic text chip.
.author-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  /* Scrollable variant used for the integrations sub-list. Caps the
     vertical footprint at roughly 5-6 rows so adding 30 integrations
     doesn't push the whole sidebar past the viewport. */
  &--scrollable {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 0.35rem;
    padding-right: 2px;
    /* Subtle scrollbar styling — keeps the panel feeling integrated. */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 9999px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #9ca3af;
    }
  }

  /* Section title between the ShopiMind row and the integrations group. */
  &__subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: 0.75rem 0.25rem 0.35rem;
    padding-top: 0.6rem;
    border-top: 1px solid #f3f4f6;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
  }

  &__subcount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #ede9fe;
    color: #6b21a8;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
  }

  /* Inline search above the scrollable list — only shown when there
     are enough partners to make searching worthwhile (cf. v-if in
     template). */
  &__search {
    position: relative;
    margin-bottom: 0.35rem;

    i {
      position: absolute;
      top: 50%;
      left: 0.55rem;
      transform: translateY(-50%);
      color: #9ca3af;
      font-size: 0.75rem;
      pointer-events: none;
    }

    input {
      width: 100%;
      padding: 0.4rem 0.5rem 0.4rem 1.8rem;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.8rem;
      background: #ffffff;
      transition: border-color 0.15s ease;

      &:focus {
        outline: none;
        border-color: #c4b5fd;
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
      }
    }
  }

  &__empty {
    padding: 0.8rem 0.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    font-style: italic;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border: 1.5px solid transparent;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }

    /* Active = selected. Border + tinted background. */
    &--active {
      background: #f5f3ff;
      border-color: #c4b5fd;
    }

    /* Integration items get an extra subtle violet stripe on the left
       when NOT selected, to flag them as "partner-published" before the
       user even hovers. */
    &--integration:not(.author-list__item--active) {
      background: linear-gradient(90deg, #faf5ff 0%, #ffffff 12%);
    }

    &--integration.author-list__item--active {
      background: #ede9fe;
      border-color: #8b5cf6;
    }
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 18px;
      height: 18px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.85rem;
      color: #6b7280;
    }
  }

  &__item--integration .author-list__avatar {
    border-color: #c4b5fd;

    i {
      color: #7c3aed;
    }
  }

  &__item--active .author-list__avatar {
    border-color: #8b5cf6;
  }

  &__label {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    flex-shrink: 0;
    min-width: 20px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9999px;
    background: #ece9fb;
    color: #6d28d9;
    font-size: 0.7rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__item--active &__label {
    font-weight: 600;
    color: #1f2937;
  }

  &__check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #8b5cf6;
    color: #ffffff;
    font-size: 0.55rem;
    flex-shrink: 0;
  }

  &__item--active:not(.author-list__item--integration) &__check {
    background: #94c748;
  }
}

// Legacy MultiSelect styling (kept in case it's still imported elsewhere).
.author-multiselect {
  width: 100%;

  :deep(.p-multiselect) {
    width: 100%;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &.p-focus {
      border-color: #94c748;
      box-shadow: 0 0 0 3px rgba(148, 199, 72, 0.1);
    }

    .p-multiselect-label {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
    }
  }
}

// Results count
.filter-results {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-top: 0.5rem;

  &__count {
    font-size: 1.5rem;
    font-weight: 700;
    color: #94c748;
    line-height: 1;
  }

  &__label {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
  }
}

// Scenarios content
.scenarios-content {
  flex: 1;
  padding: 0;
  background: transparent;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
  animation: fadeIn 0.4s ease-in-out;
}

// Scenario card
.scenario-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  /* `overflow: visible` so the partner ribbon can sit outside the card
     bounds without being clipped. The image itself gets its own
     `overflow: hidden` (cf. `&__image`) to keep its rounded corners. */
  overflow: visible;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: #94c748;
  }

  /* ============ Integration variant ============
     Strong visual distinction for items contributed by partners. The
     card gets a violet tint, a thicker ring, and a partner ribbon
     overlayed on the preview. The result is unmistakable in the grid:
     even with the eye scanning fast, "this card belongs to a partner"
     reads instantly. */
  &--integration {
    background: linear-gradient(180deg, #faf5ff 0%, #ffffff 38%);
    border: 1.5px solid #c4b5fd;
    box-shadow: 0 1px 3px rgba(139, 92, 246, 0.08);

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 10px 28px rgba(139, 92, 246, 0.22);
    }
  }

  /* The ribbon overhangs the top-left corner of the card so it doesn't
     cover the preview image. Negative `top` + `left` push it ~12px
     outside the card bounds; the card's `overflow: visible` lets it
     render there. The grid gap (cf. `.scenarios-grid`) gives enough
     room to neighbouring cards. */
  &__partner-ribbon {
    position: absolute;
    top: -12px;
    left: -10px;
    /* z-index 20 stays above `__hover-overlay` (z-index 10) so the ribbon
       remains visible while the description/tags overlay is shown. */
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 5px 12px 5px 6px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    border-radius: 9999px;
    box-shadow: 0 6px 16px rgba(91, 33, 182, 0.22);
    pointer-events: none;
  }

  &__partner-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 16px;
      height: 16px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.75rem;
      color: #7c3aed;
    }
  }

  &__partner-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;
  }

  &__partner-kicker {
    font-size: 0.55rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #7c3aed;
  }

  &__partner-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__image {
    position: relative;
    width: 100%;
    height: 180px;
    background: #f8f8f8;
    /* Round the top corners explicitly — the parent card now uses
       `overflow: visible` (so the partner ribbon can hang outside), so
       the image no longer inherits the clipping. */
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    overflow: hidden;

    &--scratch {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;

      .scratch-icon-wrapper {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #ffffff;
        border: 2px dashed #cbd5e1;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      }

      i {
        font-size: 1.25rem;
        color: #94a3b8;
        transition: all 0.3s ease;
      }

      .scenario-card:hover & .scratch-icon-wrapper {
        border-color: #94c748;
        border-style: solid;
        background: #f0fdf4;
      }

      .scenario-card:hover & i {
        color: #94c748;
      }
    }
  }

  &__preview {
    width: 100%;
    height: 100%;
    background-size: 100% auto;
    background-position: center top;
    background-repeat: no-repeat;
    transition: background-position 3s ease-in-out;

    &--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);

      i {
        font-size: 2.5rem;
        color: #bdbdbd;
      }
    }

    .scenario-card:hover & {
      background-position: center bottom;
    }
  }

  &__body {
    padding: 0.75rem 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
    line-height: 1.3;
  }

  &__author {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #64748b;
    margin-top: auto;

    i {
      font-size: 0.6875rem;
      color: #94a3b8;
    }
  }

  // Hover overlay — covers the full card
  &__hover-overlay {
    position: absolute;
    inset: 0;
    /* Match the card radius — the card is `overflow: visible` (for the ribbon),
       so without this the square overlay corners poke past the rounded border. */
    border-radius: 12px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.97);
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 0.625rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
    overflow-y: auto;
  }

  &:hover &__hover-overlay {
    opacity: 1;
    pointer-events: auto;
  }

  &__hover-legend {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #475569;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__hover-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  &__hover-actions {
    margin-top: auto;
    display: flex;
    gap: 0.5rem;

    :deep(.p-button) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;

      &.preview-btn {
        width: 36px;
        min-width: 36px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-color: #e2e8f0;
        color: #64748b;

        &:hover {
          background: #94c748;
          border-color: #94c748;
          color: #ffffff;
        }
      }

      &.choose-btn {
        background: #94c748;
        border-color: #94c748;
        font-weight: 500;
        flex: 1;
        justify-content: center;
        color: white;

        &:hover {
          background: #82b33d;
          border-color: #82b33d;
        }
      }
    }
  }

  &--scratch {
    .scenario-card__body {
      text-align: center;
    }
  }
}

// Tag badges on cards
.tag-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-size: 0.625rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;

  &--channel { background: rgba(59, 130, 246, 0.1); color: #2563eb; }
  &--feature { background: rgba(139, 92, 246, 0.1); color: #7c3aed; }
  &--type { background: rgba(245, 158, 11, 0.1); color: #d97706; }
  &--objective { background: rgba(34, 197, 94, 0.1); color: #16a34a; }
  &--more { background: #f1f5f9; color: #64748b; }
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #9ca3af;
  text-align: center;

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ==========================================
// Responsive
// ==========================================
@media (max-width: 900px) {
  .scenario-selector-layout {
    display: block;
    padding: 1.25rem;
    padding-left: 1.25rem;
  }

  .filters-sidebar {
    width: 100%;
    position: relative;
    top: 0;
    left: 0;
    max-height: none;
    margin-bottom: 1.25rem;

    &__content {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1.25rem;
      padding: 1.25rem;
    }
  }

  .filter-section {
    flex: 1;
    min-width: 200px;
  }

  .filter-results {
    margin-top: 0;
    flex: 0 0 auto;
  }

  .scenarios-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.25rem;
  }
}

@media (max-width: 600px) {
  .filters-sidebar__content {
    flex-direction: column;
  }

  .filter-section {
    min-width: 100%;
  }

  .scenarios-content {
    padding: 1rem;
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
  }

  .scenario-card__image {
    height: 160px;
  }

}
</style>

<style lang="scss">
// Override EditorModal styles when scenario selector is open
.content.bg-white:has(.scenario-selector-layout) {
  background: #f5f7fa !important;
}
</style>
